/**
 * Calculation Service
 *
 * Orchestration layer that determines which calculator to use for a client
 * based on configuration and legacy flags.
 *
 * Priority order:
 * 1. Per-class override: clientData.equity[className].value_method
 * 2. Client-level default: clientData.flags.default_value_method
 * 3. Legacy flag fallbacks (during migration): isJMH, isMH, isTSG, isSEQ
 * 4. Default calculator: private_company_409a
 */

const CalculationService = (function () {
	/**
	 * Create a new CalculationService
	 * @param {Object} registry - CalculatorRegistry instance
	 * @param {Object} legacyFlags - { isJMH, isMH, isTSG, isSEQ, etc. }
	 */
	function Service(registry, legacyFlags) {
		this.registry = registry;
		this.legacyFlags = legacyFlags || {};
		this.defaultMethodId = 'private_company_409a';
	}

	/**
	 * Get the calculator for a client (client-level default)
	 * @param {Object} clientData - Client configuration data
	 * @returns {Object} - Calculator object
	 */
	Service.prototype.getCalculatorForClient = function (clientData) {
		// 1. Check client-level config
		const configMethod = clientData && clientData.flags && clientData.flags.default_value_method;
		if (configMethod && this.registry.has(configMethod)) {
			return this.registry.get(configMethod);
		}

		// 2. Default
		return this.registry.get(this.defaultMethodId);
	};

	/**
	 * Get calculator for a specific share class (allows per-class override)
	 * @param {Object} clientData - Client configuration data
	 * @param {string} shareClass - Share class key
	 * @returns {Object} - Calculator object
	 */
	Service.prototype.getCalculatorForClass = function (clientData, shareClass) {
		// 1. Per-class override
		const classMethod = clientData && clientData.equity && clientData.equity[shareClass] && clientData.equity[shareClass].value_method;
		if (classMethod && this.registry.has(classMethod)) {
			return this.registry.get(classMethod);
		}

		// 2. Fall back to client-level
		return this.getCalculatorForClient(clientData);
	};

	/**
	 * Get calculation options for a client/class
	 * @param {Object} clientData - Client configuration data
	 * @param {string} shareClass - Share class key (optional)
	 * @returns {Object} - Options object { floorAtZero, etc. }
	 */
	Service.prototype.getOptions = function (clientData, shareClass) {
		const options = {
			floorAtZero: false,
		};

		// Check legacy flags
		if (this.legacyFlags.isSEQ) {
			options.floorAtZero = true;
		}

		// Check per-class config
		if (shareClass && clientData && clientData.equity && clientData.equity[shareClass]) {
			const classConfig = clientData.equity[shareClass];
			if (classConfig.floor_zero !== undefined) {
				options.floorAtZero = classConfig.floor_zero;
			}
		}

		// Check client-level flags
		if (clientData && clientData.flags) {
			if (clientData.flags.floor_zero !== undefined) {
				options.floorAtZero = clientData.flags.floor_zero;
			}
		}

		return options;
	};

	/**
	 * Calculate for a client using the appropriate calculator
	 * @param {Object} clientData - Client configuration data
	 * @param {Object} input - Calculator input
	 * @returns {Object} - CalculationResult
	 */
	Service.prototype.calculateForClient = function (clientData, input) {
		const calculator = this.getCalculatorForClient(clientData);
		return calculator.calculate(input);
	};

	/**
	 * Update legacy flags (useful for testing or dynamic updates)
	 * @param {Object} flags - New flag values
	 */
	Service.prototype.updateLegacyFlags = function (flags) {
		for (const key in flags) {
			if (flags.hasOwnProperty(key)) {
				this.legacyFlags[key] = flags[key];
			}
		}
	};

	return Service;
})();

export { CalculationService };
